"use client";

import { useEffect, useState } from "react";
import { useGraph } from "./GraphContext";
import { useToolLock } from "@/lib/ToolLockContext";

type AppId = "graphing" | "3d" | "geometry" | "cas" | "probability";

const APPS: Array<{ id: AppId; label: string; url: string }> = [
  { id: "graphing", label: "Grafritare", url: "https://www.geogebra.org/graphing?lang=sv" },
  { id: "probability", label: "Statistik", url: "https://www.geogebra.org/probability?lang=sv" },
  { id: "3d", label: "3D", url: "https://www.geogebra.org/3d?lang=sv" },
  { id: "geometry", label: "Geometri", url: "https://www.geogebra.org/geometry?lang=sv" },
  { id: "cas", label: "CAS", url: "https://www.geogebra.org/cas?lang=sv" },
];

export default function GraphOverlay() {
  const { open, everOpened, closeGraph } = useGraph();
  const { graphLocked: locked } = useToolLock();

  // Stäng automatiskt om elev navigerar till en uppgift utan digitala verktyg.
  useEffect(() => {
    if (locked && open) closeGraph();
  }, [locked, open, closeGraph]);

  const [active, setActive] = useState<AppId>("graphing");
  // Lazy: bara iframes användaren faktiskt klickat på är monterade. När de
  // är monterade blir de kvar — switching mellan flikar bevarar state per
  // app (display:none stoppar inte iframe-content).
  const [loaded, setLoaded] = useState<Set<AppId>>(new Set(["graphing"]));

  function selectApp(id: AppId) {
    setActive(id);
    setLoaded((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="GeoGebra graf-verktyg"
      aria-hidden={!open}
      onClick={closeGraph}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        display: open ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "96vw",
          height: "94vh",
          maxWidth: "1500px",
          background: "var(--color-bg)",
          borderRadius: "0.5rem",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 16px 48px rgba(0,0,0,0.32)",
        }}
      >
        {/* Header — orange band med flik-strip + stäng-knapp.
            Samma design som FormelbladButton + ExtraMaterialModal:
            aktiv flik = off-white (var(--color-bg)), inaktiv = sand
            (var(--color-sidebar-bg)), rundade hörn upptill. */}
        <div
          style={{
            padding: "0.5rem 0.75rem 0 0.75rem",
            background: "var(--color-nav-bg)",
            borderBottom: "1px solid color-mix(in oklab, var(--color-nav-bg) 70%, black)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "0.25rem",
              fontSize: "0.75rem",
            }}
          >
            {APPS.map((app) => {
              const isActive = active === app.id;
              return (
                <button
                  key={app.id}
                  type="button"
                  onClick={() => selectApp(app.id)}
                  title={`Byt till ${app.label}`}
                  style={{
                    padding: "0.25rem 0.625rem",
                    border: "1px solid color-mix(in oklab, var(--color-nav-bg) 65%, black)",
                    borderBottom: isActive
                      ? "1px solid var(--color-bg)"
                      : "1px solid color-mix(in oklab, var(--color-nav-bg) 65%, black)",
                    borderTopLeftRadius: "0.25rem",
                    borderTopRightRadius: "0.25rem",
                    color: isActive ? "var(--color-text)" : "var(--color-text-muted)",
                    background: isActive ? "var(--color-bg)" : "var(--color-sidebar-bg)",
                    marginBottom: "-1px",
                    cursor: "pointer",
                    transition: "color 0.15s, background 0.15s",
                  }}
                >
                  {app.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={closeGraph}
            aria-label="Stäng"
            title="Stäng graf-verktyget — ditt arbete sparas tills sidan laddas om"
            style={{
              padding: "0.25rem",
              marginBottom: "0.25rem",
              color: "var(--color-text)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
            >
              <line x1="3" y1="3" x2="13" y2="13" />
              <line x1="3" y1="13" x2="13" y2="3" />
            </svg>
          </button>
        </div>

        {/* Iframes — alla laddade appar bor här, bara den aktiva visas. Inget
            renderas alls innan första öppning av modalen så bandbredd sparas
            på sidor där användaren aldrig öppnar verktyget.
            marginTop=-72px + overflow:hidden på parent skär bort GeoGebras
            egen tomma topp-padding så våra tabs sluter direkt mot deras UI. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {everOpened &&
            APPS.filter((a) => loaded.has(a.id)).map((app) => (
              <iframe
                key={app.id}
                src={app.url}
                title={app.label}
                style={{
                  flex: 1,
                  border: 0,
                  width: "100%",
                  minHeight: "calc(100% + 72px)",
                  marginTop: "-72px",
                  display: active === app.id ? "block" : "none",
                }}
                allow="clipboard-read; clipboard-write"
              />
            ))}
        </div>
      </div>
    </div>
  );
}
